import React, { useState, useRef, useEffect } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { FloppyDisk, Camera, User, Key, Eye, EyeSlash, WhatsappLogo } from "@phosphor-icons/react";
import CardShareSection from "../../components/CardShareSection";

/**
 * IMPORTANTE: Este componente se define FUERA de ProfileTab.
 * Si lo ponés adentro, React lo recrea en cada render y los inputs
 * se desmontan/remontan en cada keystroke → pierde foco → se cierra
 * el teclado en mobile (iOS y Android).
 */
function Field({ label, name, type = "text", placeholder = "", form, setForm, canEdit }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted block mb-2">{label}</label>
      <input
        className="input-base"
        type={type}
        value={form[name] ?? ""}
        onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
        disabled={!canEdit}
        placeholder={placeholder}
        data-testid={`profile-${name}`}
      />
    </div>
  );
}

export default function ProfileTab({ target, setTarget, isAdmin, isOwner }) {
  const canEdit = isAdmin || isOwner;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(target.profile?.avatar_url || "");
  const [form, setForm] = useState({
    name: target.name || "",
    birthdate: target.profile?.birthdate || "",
    height_cm: target.profile?.height_cm || "",
    weight_kg: target.profile?.weight_kg || "",
    phone: target.profile?.phone || "",
    sport: target.profile?.sport || "",
    sex: target.profile?.sex || "",
    notes: target.profile?.notes || "",
    monthly_fee: target.profile?.monthly_fee ?? "",
    plan_type: target.profile?.plan_type ?? "",
    assigned_profesor_id: target.profile?.assigned_profesor_id ?? "",
    role: target.role || "user",
  });
  const [saving, setSaving] = useState(false);
  const [profesores, setProfesores] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/profesores");
        setProfesores(data || []);
      } catch { /* silent */ }
    })();
  }, [isAdmin]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagen muy grande (máx 5MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      // Save to profile immediately
      const { data: updatedUser } = await api.put(`/users/${target.user_id}`, {
        profile: { ...target.profile, avatar_url: fullUrl },
      });
      setAvatarUrl(fullUrl);
      setTarget(updatedUser);
      toast.success("Foto actualizada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm("¿Eliminar foto de perfil?")) return;
    try {
      const { data: updatedUser } = await api.put(`/users/${target.user_id}`, {
        profile: { ...target.profile, avatar_url: null },
      });
      setAvatarUrl("");
      setTarget(updatedUser);
      toast.success("Foto eliminada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // 1. Role change (admin-only, separate endpoint)
      if (isAdmin && form.role && form.role !== target.role) {
        await api.put(`/users/${target.user_id}/role`, { role: form.role });
      }
      // 2. Assigned profesor (admin-only, separate endpoint). Solo si el target NO es admin ni profesor.
      const targetIsAthlete = (form.role || target.role) === "user";
      if (isAdmin && targetIsAthlete) {
        const prev = target.profile?.assigned_profesor_id || "";
        const next = form.assigned_profesor_id || "";
        if (prev !== next) {
          await api.put(`/users/${target.user_id}/assigned-profesor`, {
            profesor_id: next || null,
          });
        }
      }
      // 3. Perfil general
      const { data } = await api.put(`/users/${target.user_id}`, {
        name: form.name,
        profile: {
          avatar_url: avatarUrl || null,
          birthdate: form.birthdate,
          height_cm: form.height_cm ? Number(form.height_cm) : null,
          weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
          phone: form.phone,
          sport: form.sport,
          sex: form.sex,
          notes: form.notes,
          monthly_fee: isAdmin
            ? (form.monthly_fee ? Number(form.monthly_fee) : null)
            : (target.profile?.monthly_fee ?? null),
          plan_type: isAdmin
            ? (form.plan_type || null)
            : (target.profile?.plan_type ?? null),
        },
      });
      setTarget(data);
      toast.success("Perfil actualizado");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Field ahora es un componente externo (ver arriba del archivo).
  // Se pasan form/setForm/canEdit como props para mantener estabilidad en cada render.

  return (
    <>
      <form onSubmit={handleSave} className="card-base p-6 sm:p-8" data-testid="profile-form">
      <h2 className="font-display uppercase font-black text-2xl mb-6">Datos Personales</h2>

      {/* Avatar uploader */}
      <div className="flex items-center gap-5 mb-8 pb-6 border-b border-app" data-testid="avatar-section">
        <div className="relative w-24 h-24 bg-[#0A0A0A] border border-app flex items-center justify-center overflow-hidden flex-shrink-0">
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" data-testid="avatar-img" />
          ) : (
            <User size={40} weight="bold" className="text-muted" />
          )}
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-muted mb-1">Foto de perfil</div>
          <div className="text-sm text-muted mb-3">JPG, PNG o WebP · máx 5MB</div>
          {canEdit && (
            <div className="flex gap-2 flex-wrap">
              <label className="btn-secondary cursor-pointer flex items-center gap-2" data-testid="upload-avatar-btn">
                <Camera size={16} weight="bold" />
                {uploading ? "Subiendo..." : avatarUrl ? "Cambiar foto" : "Subir foto"}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
              </label>
              {avatarUrl && (
                <button type="button" onClick={handleRemoveAvatar} className="btn-ghost text-xs uppercase tracking-widest" data-testid="remove-avatar-btn">
                  Eliminar foto
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Nombre completo" name="name" form={form} setForm={setForm} canEdit={canEdit} />
        <Field label="Fecha de nacimiento" name="birthdate" type="date" form={form} setForm={setForm} canEdit={canEdit} />
        <Field label="Altura (cm)" name="height_cm" type="number" placeholder="178" form={form} setForm={setForm} canEdit={canEdit} />
        <Field label="Peso (kg)" name="weight_kg" type="number" placeholder="72" form={form} setForm={setForm} canEdit={canEdit} />
        <div>
          <label className="text-xs uppercase tracking-widest text-muted block mb-2">Teléfono</label>
          <div className="flex gap-2">
            <input
              type="tel"
              className="input-base flex-1"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              disabled={!canEdit}
              data-testid="profile-phone"
              placeholder="+54 9 11 1234-5678"
            />
            {form.phone && (() => {
              const cleaned = form.phone.replace(/[^\d]/g, "");
              if (!cleaned) return null;
              const url = `https://wa.me/${cleaned}`;
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary !py-2 !px-3 flex items-center gap-1 !border-[#25D366] text-[#25D366] hover:!bg-[#25D366]/10 flex-shrink-0"
                  title={`Abrir WhatsApp con ${form.phone}`}
                  data-testid="profile-phone-whatsapp"
                >
                  <WhatsappLogo size={18} weight="bold" />
                  <span className="hidden sm:inline text-xs uppercase tracking-widest font-bold">WhatsApp</span>
                </a>
              );
            })()}
          </div>
        </div>
        <Field label="Deporte" name="sport" placeholder="Fútbol, Rugby..." form={form} setForm={setForm} canEdit={canEdit} />
        <div>
          <label className="text-xs uppercase tracking-widest text-muted block mb-2">Sexo</label>
          <select
            className="input-base"
            value={form.sex}
            onChange={(e) => setForm({ ...form, sex: e.target.value })}
            disabled={!canEdit}
            data-testid="profile-sex"
          >
            <option value="">—</option>
            <option value="MASCULINO">Masculino</option>
            <option value="FEMENINO">Femenino</option>
          </select>
        </div>
        {isAdmin && (
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">Rol</label>
            <select
              className="input-base"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="profile-role"
            >
              <option value="user">Atleta</option>
              <option value="profesor">Profesor</option>
              <option value="admin">Administrador</option>
            </select>
            <div className="text-[10px] text-muted mt-1">
              Un profesor ve solo sus atletas asignados y no accede a Pagos ni configuración.
            </div>
          </div>
        )}
        {isAdmin && form.role === "user" && (
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">
              Profesor asignado
            </label>
            <select
              className="input-base"
              value={form.assigned_profesor_id || ""}
              onChange={(e) => setForm({ ...form, assigned_profesor_id: e.target.value })}
              data-testid="profile-assigned-profesor"
            >
              <option value="">Sin asignar</option>
              {profesores.map((p) => (
                <option key={p.user_id} value={p.user_id}>{p.name}</option>
              ))}
            </select>
            <div className="text-[10px] text-muted mt-1">
              El profesor podrá ver y gestionar este atleta.
            </div>
          </div>
        )}
        {isAdmin && (
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">
              Plan de cuota
            </label>
            <select
              className="input-base"
              value={form.plan_type}
              onChange={(e) => {
                const p = e.target.value;
                const presetPrice = { "3x_semana": 70000, "2x_semana": 60000, "individual": 12000 }[p];
                setForm({
                  ...form,
                  plan_type: p,
                  monthly_fee: presetPrice != null ? String(presetPrice) : form.monthly_fee,
                });
              }}
              data-testid="profile-plan-type"
            >
              <option value="">Cuota personalizada (legacy)</option>
              <option value="3x_semana">3 veces por semana — $70.000</option>
              <option value="2x_semana">2 veces por semana — $60.000</option>
              <option value="individual">Clase individual — $12.000 (paga por sesión)</option>
            </select>
            <div className="text-[10px] text-muted mt-1">
              {form.plan_type === "individual"
                ? "Sin vencimiento: el atleta paga cada vez que viene."
                : "El vencimiento se calcula como último pago + 1 mes."}
            </div>
          </div>
        )}
        {isAdmin && (
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">
              Monto de la cuota (ARS)
            </label>
            <input
              type="number"
              min="0"
              step="100"
              className="input-base"
              value={form.monthly_fee}
              onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })}
              placeholder="25000"
              data-testid="profile-monthly-fee"
            />
            <div className="text-[10px] text-muted mt-1">
              {form.plan_type
                ? "Autocompletado desde el plan. Podés sobreescribir si querés."
                : "Aparece como monto sugerido al pagar con Mercado Pago."}
            </div>
          </div>
        )}
      </div>
      <div className="mt-5">
        <label className="text-xs uppercase tracking-widest text-muted block mb-2">Notas</label>
        <textarea
          className="input-base"
          rows="3"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          disabled={!canEdit}
          data-testid="profile-notes"
        />
      </div>
      {canEdit && (
        <button type="submit" disabled={saving} className="btn-primary mt-6 flex items-center gap-2" data-testid="save-profile-btn">
          <FloppyDisk size={18} weight="bold" />
          {saving ? "Guardando..." : "Guardar Cambios"}
        </button>
      )}
      </form>
      {isOwner && (
        <div className="mt-6">
          <CardShareSection userId={target.user_id} athleteName={target.name} />
        </div>
      )}
      {isOwner && (
        <div className="card-base p-6 sm:p-8 mt-6" data-testid="security-card">
          <ChangePasswordSection />
        </div>
      )}
    </>
  );
}

// Mismo patrón: PwdInput definido fuera del componente para mantener estables
// los inputs entre renders y evitar que se cierre el teclado en mobile.
function PwdInput({ name, label, visible, onToggle, testid, form, setForm }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted block mb-2">{label}</label>
      <div className="relative">
        <input
          className="input-base pr-12"
          type={visible ? "text" : "password"}
          value={form[name]}
          onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
          autoComplete={name === "current_password" ? "current-password" : "new-password"}
          data-testid={testid}
          required
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-white transition-colors"
          tabIndex={-1}
        >
          {visible ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
        </button>
      </div>
    </div>
  );
}

function ChangePasswordSection() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const reset = () => {
    setForm({ current_password: "", new_password: "", confirm_password: "" });
    setShow({ current: false, next: false, confirm: false });
  };

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!form.current_password || !form.new_password) {
      toast.error("Completá todos los campos");
      return;
    }
    if (form.new_password.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Contraseña actualizada");
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };


  return (
    <div data-testid="change-password-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Key size={20} weight="duotone" className="text-primary-app" />
          <div>
            <h3 className="font-display uppercase font-black text-xl tracking-tighter">Seguridad</h3>
            <div className="text-xs text-muted">Cambiá tu contraseña privada</div>
          </div>
        </div>
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="btn-secondary flex items-center gap-2"
            data-testid="open-change-password-btn"
          >
            <Key size={16} weight="bold" />
            Cambiar contraseña
          </button>
        )}
      </div>

      {open && (
        <div className="space-y-4 bg-[#0A0A0A] border border-app p-5">
          <PwdInput
            name="current_password"
            label="Contraseña actual"
            visible={show.current}
            onToggle={() => setShow({ ...show, current: !show.current })}
            testid="cp-current"
            form={form}
            setForm={setForm}
          />
          <PwdInput
            name="new_password"
            label="Nueva contraseña (mín. 6 caracteres)"
            visible={show.next}
            onToggle={() => setShow({ ...show, next: !show.next })}
            testid="cp-new"
            form={form}
            setForm={setForm}
          />
          <PwdInput
            name="confirm_password"
            label="Confirmar nueva contraseña"
            visible={show.confirm}
            onToggle={() => setShow({ ...show, confirm: !show.confirm })}
            testid="cp-confirm"
            form={form}
            setForm={setForm}
          />
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
              data-testid="cp-submit"
            >
              <FloppyDisk size={16} weight="bold" />
              {saving ? "Guardando..." : "Guardar contraseña"}
            </button>
            <button
              type="button"
              onClick={() => { reset(); setOpen(false); }}
              className="btn-ghost"
              data-testid="cp-cancel"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
